// 🧭 EPLProgressStepper - Componente de progreso con microanimaciones EPL
// 📋 Implementa Section 4.3-4.4 de EPL_UX_UI_POLICY.md

import React from 'react';
import { CheckCircle } from 'lucide-react';
import { getPhaseColors } from '../config/eplColors';

/**
 * Stepper de progreso EPL con microanimaciones y comportamiento responsive
 */
const EPLProgressStepper = ({ 
  variant = 'default',  // Nueva prop: 'default' o 'transparent'
  steps = [],           // Array de pasos: [{ id, title, phase, completed, active }]
  currentIndex = 0      // Índice actual
}) => {
  
  // Clases base dependiendo de la variante
  const containerClasses = variant === 'transparent' 
    ? 'mb-4 bg-transparent shadow-none' 
    : 'mb-8';
  
  return (
    <div className={containerClasses}>
      {/* Desktop: Timeline horizontal perfectamente alineado */}
      <div className="hidden md:flex items-start justify-between">
        {steps.map((step, index) => {
          const colors = getPhaseColors(step.phase);
          const isCompleted = step.completed;
          const isActive = index === currentIndex;
          const isFuture = index > currentIndex;
          
          return (
            <React.Fragment key={step.id}>
              {/* Contenedor de paso - altura fija para alineación perfecta */}
              <div className="flex flex-col items-center justify-start min-h-20">
                {/* Círculo del paso - posición fija */}
                <div 
                  className={`
                    w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 shrink-0
                    ${isCompleted 
                      ? 'bg-green-500 text-white' 
                      : isActive 
                        ? 'bg-blue-500 text-white shadow-lg' 
                        : 'bg-gray-200 text-gray-500'
                    }
                    ${isActive ? 'scale-110' : 'scale-100'}
                  `}
                  style={{
                    backgroundColor: isActive && !isCompleted ? colors.primary : undefined
                  }}
                >
                  {isCompleted ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <span className="text-sm font-semibold">{index + 1}</span>
                  )}
                </div>
                
                {/* Título del paso - altura consistente */}
                <span className={`
                  mt-2 text-xs font-medium text-center max-w-20 leading-tight h-8 flex items-center
                  ${isCompleted 
                    ? 'text-green-600' 
                    : isActive 
                      ? 'text-blue-600 font-semibold' 
                      : 'text-gray-500'
                  }
                `}>
                  {step.title}
                </span>
              </div>
              
              {/* Línea de conexión */}
              {index < steps.length - 1 && (
                <div className={`
                  flex-1 h-0.5 mx-2 transition-all duration-500
                  ${isCompleted 
                    ? 'bg-green-500' 
                    : index < currentIndex 
                      ? 'bg-green-500'
                      : 'bg-gray-200'
                  }
                `} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Móvil: Horizontal inteligente con scroll suave */}
      <div className="md:hidden">
        {/* Indicador de contexto actual - muy compacto */}
        <div className="flex items-center justify-center mb-3 bg-gray-50 rounded-md p-2">
          <span className="text-xs font-medium text-gray-600">
            {steps[currentIndex]?.title} • Paso {currentIndex + 1}/{steps.length}
          </span>
        </div>

        {/* Timeline horizontal - FORZADO en una sola línea */}
        <div className="w-full overflow-x-auto scrollbar-hide">
          <div className="inline-flex items-center gap-1 px-2 py-2 min-w-full">
            {steps.map((step, index) => {
              const colors = getPhaseColors(step.phase);
              const isCompleted = step.completed;
              const isActive = index === currentIndex;
              
              return (
                <React.Fragment key={step.id}>
                  {/* Contenedor de paso - ancho fijo sin wrap */}
                  <div className="flex flex-col items-center justify-start" style={{ width: '56px', flexShrink: 0 }}>
                    {/* Círculo perfectamente centrado */}
                    <div className={`
                      w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300
                      ${isCompleted 
                        ? 'bg-green-500 text-white' 
                        : isActive 
                          ? 'bg-blue-500 text-white shadow-md' 
                          : 'bg-gray-200 text-gray-500'
                      }
                    `}>
                      {isCompleted ? (
                        <CheckCircle className="w-3.5 h-3.5" />
                      ) : (
                        <span className="text-xs font-semibold">{index + 1}</span>
                      )}
                    </div>
                    
                    {/* Título ultra-compacto - ancho fijo */}
                    <span className={`
                      mt-1 text-xs text-center leading-tight w-full overflow-hidden
                      ${isCompleted 
                        ? 'text-green-600 font-medium' 
                        : isActive 
                          ? 'text-blue-600 font-semibold' 
                          : 'text-gray-500'
                      }
                    `} style={{ 
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      textOverflow: 'ellipsis',
                      height: '32px'
                    }}>
                      {step.title.length > 8 ? step.title.split(' ')[0] : step.title}
                    </span>
                  </div>
                  
                  {/* Línea de conexión - altura fija */}
                  {index < steps.length - 1 && (
                    <div className={`
                      w-3 h-0.5 transition-all duration-300
                      ${isCompleted || index < currentIndex 
                        ? 'bg-green-500' 
                        : 'bg-gray-200'
                      }
                    `} style={{ flexShrink: 0, marginTop: '14px' }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Porcentaje general */}
      <div className="text-center mt-4">
        <span className="text-sm font-medium text-gray-600">
          Progreso del laboratorio
        </span>
        <div className="text-lg font-bold text-gray-900 mt-1">
          {Math.round((steps.filter(s => s.completed).length / steps.length) * 100)}% completado
        </div>
      </div>
    </div>
  );
};

export default EPLProgressStepper;